import React, { useState } from 'react';
import Icon from '@/components/Icon';
import SettingsForm from './SettingsForm';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/auth/AuthContext';

/**
 * Кастомная панель раздела «Почта (SMTP)».
 *
 * Сверху — стандартная форма настроек (через переиспользование
 * SettingsForm с пропсом section="mail"). Снизу — кнопка
 * «Проверить настройки»: PHP зовёт Node, Node делает handshake к SMTP
 * без отправки письма (`nodemailer.verify()`). По результату показываем
 * зелёный «Соединение установлено» или красное сообщение об ошибке.
 *
 * Проверяются текущие сохранённые настройки. Если оператор поправил
 * поля, но не сохранил — кнопка проверит то, что в БД. UI явно об этом
 * предупреждает в подсказке.
 */
export default function MailSettingsPanel() {
  const can = useCan();
  const canRead = can('settings.mail', 'read');

  if (!canRead) {
    return (
      <div className="settings-card">
        <h2>Нет доступа</h2>
        <p>У вас нет права на чтение настроек почты.</p>
      </div>
    );
  }

  return (
    <>
      <SettingsForm section="mail" />
      <MailTestBlock />
    </>
  );
}

function MailTestBlock() {
  const [testing, setTesting] = useState(false);
  const [result, setResult]   = useState(null); // { ok: true } | { ok: false, error: '...' }

  const runTest = async () => {
    setTesting(true); setResult(null);
    try {
      const res = await api.post('/api/settings/mail/test', {});
      setResult(res);
    } catch (e) {
      // ApiError формы { code, message, payload? } — для нас важно показать message.
      // 502 от бэкенда тоже сюда упадёт.
      const msg = e instanceof ApiError
        ? (e.payload?.error || e.message)
        : (e?.message || 'Не удалось проверить');
      setResult({ ok: false, error: String(msg) });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="settings-card" style={{ marginTop: 16 }}>
      <div className="settings-card__head">
        <h2 className="settings-card__title">Проверка SMTP</h2>
        <p className="settings-card__sub">
          Проверим соединение и авторизацию на SMTP-сервере без отправки
          письма. Используются <b>сохранённые</b> значения — если вы
          поправили поля выше, сначала нажмите «Сохранить».
        </p>
      </div>

      {result && result.ok && (
        <div
          style={{
            padding: '10px 14px',
            background: 'rgba(34,197,94,0.10)',
            color: 'var(--green-700)',
            border: '1px solid rgba(34,197,94,0.3)',
            borderRadius: 6,
            fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          <Icon name="check" size={14} /> Соединение установлено, авторизация прошла.
        </div>
      )}
      {result && !result.ok && (
        <div
          style={{
            padding: '10px 14px',
            background: 'rgba(239,68,68,0.08)',
            color: 'var(--red-fg)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 6,
            fontSize: 13,
            display: 'flex', alignItems: 'flex-start', gap: 8,
          }}
        >
          <Icon name="alert" size={14} />
          <div>
            <div><b>Не удалось:</b> {result.error || 'неизвестная ошибка'}</div>
            <div style={{ marginTop: 4, color: 'var(--fg-muted)', fontSize: 12 }}>
              Проверьте хост/порт/шифрование и креды. Если используется
              самоподписанный сертификат — включите «Не проверять TLS-сертификат»
              (но не для прода).
            </div>
          </div>
        </div>
      )}

      <div className="settings-card__foot">
        <div className="settings-card__spacer" />
        <button
          type="button"
          className="btn btn--sm btn--primary"
          onClick={runTest}
          disabled={testing}
        >
          {testing ? 'Проверяем…' : 'Проверить настройки'}
        </button>
      </div>
    </div>
  );
}
